import { Link, useOutletContext } from "@remix-run/react";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import type { CartItem } from "~/routes/_storefront";
import { getColorHex, SIZE_ORDER, fixImgUrl } from "~/utils/storefront-constants";
import { resolveProductMedia, resolveFeaturedImage } from "~/utils/productMedia";
import { formatStorefrontPrice } from "~/utils/money";
import { t } from "~/utils/translations";
import type { Locale } from "~/utils/locale";
import { displayTitle as formatTitleForLocale } from "~/utils/productTitle";
import { getProductBaseName } from "~/utils/productSiblings";

export interface ProductCardVariant {
  id: string;
  option1: string | null;
  option2: string | null;
  price: any;
  compareAtPrice: any;
  inventoryQuantity: number;
}

export interface ProductCardSibling {
  id: string;
  handle: string | null;
  title: string;
  titleEn?: string | null;
  featuredImageUrl: string | null;
  /**
   * Per-sibling media payload (same shapes as ProductCardProduct). Lets the
   * card show the sibling's full swipeable gallery when its swatch is
   * selected instead of collapsing to a single featured image.
   */
  images?: unknown;
  mediaOverrides?: unknown;
  backupImages?: unknown;
  retailPrice: number;
  retailComparePrice?: number | null;
  color: string | null;
  preOrderEnabled?: boolean;
  totalInventory?: number;
  /** True for the entry that represents the same product the card was rendered for. */
  isCurrent?: boolean;
}

export interface ProductCardProduct {
  id: string;
  handle: string | null;
  title: string;
  titleEn?: string | null;
  featuredImageUrl: string | null;
  images?: any; // JSON: string[] | { url: string; altText?: string; color?: string }[] | null
  retailPrice: any;
  retailComparePrice: any;
  tags: string | null;
  variants: ProductCardVariant[];
  reviewAvg?: number;
  reviewCount?: number;
  preOrderEnabled?: boolean;
}

const MAX_SWATCHES = 6;

export function ProductCard({
  product,
  enablePeek = false,
  siblings,
  locale = "vi",
  onQuickView,
}: {
  product: ProductCardProduct;
  enablePeek?: boolean;
  siblings?: ProductCardSibling[];
  locale?: Locale;
  onQuickView?: () => void;
}) {
  const { addToCart, wishlist, toggleWishlist, currency } = useOutletContext<{
    addToCart: (item: CartItem) => void;
    wishlist?: string[];
    toggleWishlist?: (productId: string) => void;
    currency: string;
  }>();
  const isWishlisted = wishlist?.includes(product.id) ?? false;
  const [isHovered, setIsHovered] = useState(false);
  const [addedSize, setAddedSize] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [hasPeeked, setHasPeeked] = useState(false);
  const [wishlistBounce, setWishlistBounce] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isDragging = useRef(false);
  const containerWidth = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  // ─── Sibling swatch state ─── //
  const [selectedSiblingId, setSelectedSiblingId] = useState<string | null>(null);
  // Aritzia-style hover preview: while the cursor is over a swatch we show
  // that variant's gallery in the card without committing the click. The
  // sentinel string "__base__" means "hovering the current/own swatch" so
  // we can preview the base product even when something else is selected.
  const [hoveredSiblingId, setHoveredSiblingId] = useState<string | null>(null);
  const activeSibling = useMemo(() => {
    if (!selectedSiblingId || !siblings) return null;
    return siblings.find((s) => s.id === selectedSiblingId) || null;
  }, [selectedSiblingId, siblings]);
  // Image preview prefers hover over click. Hovering "__base__" forces the
  // base product gallery; hovering a sibling id surfaces that sibling.
  // Falls through to the clicked sibling (or null) when no hover is active.
  const imagePreviewSibling = useMemo(() => {
    if (hoveredSiblingId === "__base__") return null;
    if (hoveredSiblingId && siblings) {
      return siblings.find((s) => s.id === hoveredSiblingId) || activeSibling;
    }
    return activeSibling;
  }, [hoveredSiblingId, siblings, activeSibling]);

  // Derive display values: prefer sibling override if selected.
  // Locale-aware via the shared helper, which prefers titleEn → falls back to
  // stripping the trailing " (Vietnamese)" parenthetical when titleEn is null
  // → finally returns the raw title unchanged. This matches what cart and
  // checkout do, so titles look consistent everywhere on the storefront.
  const displayTitle = activeSibling
    ? formatTitleForLocale(activeSibling.title, locale, activeSibling.titleEn ?? null)
    : formatTitleForLocale(product.title, locale, product.titleEn ?? null);
  const displayImage = activeSibling?.featuredImageUrl || product.featuredImageUrl;
  const displayHandle = activeSibling?.handle || product.handle || product.id;
  const displayPrice = activeSibling
    ? activeSibling.retailPrice
    : (product.retailPrice ? Number(product.retailPrice) : Number(product.variants[0]?.price || 0));
  const displayComparePrice = activeSibling
    ? (activeSibling.retailComparePrice ? Number(activeSibling.retailComparePrice) : null)
    : (product.retailComparePrice ? Number(product.retailComparePrice) : null);

  // Diolo rebrand: cards show "Base Name" + colour as a separate subtitle
  // row (catalog titles are "Base - Colour (Vietnamese)").
  const displayBaseTitle = getProductBaseName(displayTitle) || displayTitle;
  const displayColourName = (() => {
    if (activeSibling?.color) return activeSibling.color;
    const cur = siblings?.find((s) => s.isCurrent);
    if (cur?.color) return cur.color;
    const raw = activeSibling ? activeSibling.title : product.title;
    const base = getProductBaseName(raw);
    if (!base || base === raw) return null;
    const rest = raw.slice(base.length).replace(/^\s*-\s*/, "").replace(/\(.*?\)/g, "").trim();
    return rest || null;
  })();

  // Detect if option1 contains sizes (not colors) — single-option products
  const rawOpt1 = [...new Set(product.variants.map((v) => v.option1).filter(Boolean))] as string[];
  const rawOpt2 = [...new Set(product.variants.map((v) => v.option2).filter(Boolean))] as string[];
  const opt1IsSize = rawOpt2.length === 0 && rawOpt1.length > 0 && rawOpt1.every((v) => v in SIZE_ORDER);
  const colors = opt1IsSize ? [] : rawOpt1;
  const sizes = opt1IsSize ? rawOpt1 : rawOpt2;
  const price = displayPrice;
  const comparePrice = displayComparePrice;
  const isNew = product.tags?.includes("new");
  // Audit 2026-04-30: tightened threshold from <=5 to <=3 AND added a
  // deterministic 50% hash-gate so the "Selling Fast" badge only
  // surfaces on roughly half of qualifying products. Merchant feedback:
  // density was too high and the signal was losing meaning.
  const _hashGate = ((): boolean => {
    let h = 0;
    for (let i = 0; i < product.id.length; i++) h = (h * 31 + product.id.charCodeAt(i)) | 0;
    return (h & 1) === 0;
  })();
  const lowStock =
    product.variants.some((v) => v.inventoryQuantity > 0 && v.inventoryQuantity <= 3) &&
    _hashGate;

  // ─── Pre-order / Sold Out badges ─── //
  const isPreOrder = !!(product.preOrderEnabled || activeSibling?.preOrderEnabled);
  const totalInventory = activeSibling
    ? (activeSibling.totalInventory ?? 0)
    : product.variants.reduce((sum, v) => sum + v.inventoryQuantity, 0);
  const isSoldOut = totalInventory === 0 && !isPreOrder;

  // Normalize images via the shared helper so the render priority is
  // identical to the PDP + QuickView. Reads `mediaOverrides` → Sapo `images`
  // (≥2) → `backupImages` → lone Sapo image → `featuredImageUrl`.
  // `imagePreviewSibling` is hover-aware (Aritzia style) so the gallery
  // updates while the cursor is on a swatch, then reverts on mouse-leave.
  const imageList: string[] = useMemo(() => {
    if (imagePreviewSibling) {
      const resolved = resolveProductMedia({
        mediaOverrides: imagePreviewSibling.mediaOverrides,
        images: imagePreviewSibling.images,
        backupImages: imagePreviewSibling.backupImages,
        featuredImageUrl: imagePreviewSibling.featuredImageUrl,
        title: imagePreviewSibling.title,
      }).map((m) => m.url);
      if (resolved.length > 0) return resolved;
      return imagePreviewSibling.featuredImageUrl ? [fixImgUrl(imagePreviewSibling.featuredImageUrl)] : [];
    }
    return resolveProductMedia(product as any).map((m) => m.url);
  }, [product, imagePreviewSibling]);

  // Reset gallery index whenever the previewed/selected sibling changes,
  // so hovering or clicking a swatch always lands on the first photo of
  // that colour rather than remembering the previous colour's position.
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [imagePreviewSibling?.id]);

  // ─── Peek animation ───
  useEffect(() => {
    if (!enablePeek || hasPeeked || imageList.length <= 1) return;
    const el = cardRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      const w = el.getBoundingClientRect().width;
      if (w <= 0) return;
      setIsTransitioning(true);
      setDragOffset(-w * 0.85);
      const snapBack = setTimeout(() => {
        setDragOffset(0);
        setHasPeeked(true);
      }, 1200);
      return () => clearTimeout(snapBack);
    }, 800);
    return () => clearTimeout(timer);
  }, [enablePeek, hasPeeked, imageList.length]);

  // ─── Real-time drag touch handlers ───
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const el = cardRef.current;
    if (el) containerWidth.current = el.getBoundingClientRect().width;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = false;
    setIsTransitioning(false);
    setDragOffset(0);
  }, []);

  // Audit 2026-05-15: native non-passive touchmove listener so we can
  // preventDefault() to stop the page from vertically scrolling DURING
  // a horizontal swipe. React's synthetic onTouchMove is passive by
  // default in React 17+, so calling e.preventDefault() inside the
  // React handler is a no-op — the page still scrolled vertically while
  // the user was horizontally panning across product images, causing
  // the "jitter" merchant feedback. Pairs with `touch-action: pan-y`
  // on the swipeable container (set inline below) which tells the
  // browser "only do vertical pan on this element by default" — so
  // horizontal swipes never fight with browser horizontal-scroll-snap
  // either. We still allow the page to scroll vertically when the
  // user's gesture is dominantly vertical (i.e. before isDragging
  // flips true at the 10px horizontal-dominant threshold).
  useEffect(() => {
    const el = cardRef.current;
    if (!el || imageList.length <= 1) return;

    const onMove = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;

      // Decision threshold — 10px so a small natural finger wiggle
      // doesn't lock the gesture immediately. Match the React handler.
      if (
        !isDragging.current &&
        Math.abs(deltaX) > Math.abs(deltaY) &&
        Math.abs(deltaX) > 10
      ) {
        isDragging.current = true;
      }

      // Once we've committed to a horizontal drag, block all default
      // browser behaviour for this gesture — most importantly the
      // vertical page scroll that was causing the jitter.
      if (isDragging.current) {
        e.preventDefault();
      }
    };

    el.addEventListener("touchmove", onMove, { passive: false });
    return () => el.removeEventListener("touchmove", onMove);
  }, [imageList.length]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = e.touches[0].clientY - touchStartY.current;

    if (!isDragging.current && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      isDragging.current = true;
    }

    if (isDragging.current) {
      let clampedDelta = deltaX;
      if ((currentImageIndex === 0 && deltaX > 0) || (currentImageIndex === imageList.length - 1 && deltaX < 0)) {
        clampedDelta = deltaX * 0.3;
      }
      setDragOffset(clampedDelta);
    }
  }, [currentImageIndex, imageList.length]);

  const handleTouchEnd = useCallback(
    (_e: React.TouchEvent) => {
      setIsTransitioning(true);
      const w = containerWidth.current || 300;
      const SNAP_THRESHOLD = w * 0.2;

      if (isDragging.current && Math.abs(dragOffset) > SNAP_THRESHOLD) {
        if (dragOffset < 0 && currentImageIndex < imageList.length - 1) {
          setCurrentImageIndex((prev) => prev + 1);
        } else if (dragOffset > 0 && currentImageIndex > 0) {
          setCurrentImageIndex((prev) => prev - 1);
        }
      }
      setDragOffset(0);
      touchStartX.current = null;
      touchStartY.current = null;
      setTimeout(() => { isDragging.current = false; }, 50);
    },
    [currentImageIndex, imageList.length, dragOffset],
  );

  const handleLinkClick = useCallback((e: React.MouseEvent) => {
    if (isDragging.current) {
      e.preventDefault();
      isDragging.current = false;
    }
  }, []);

  const isSizeInStock = useCallback(
    (size: string) => {
      if (opt1IsSize) return product.variants.some((v) => v.option1 === size && v.inventoryQuantity > 0);
      return product.variants.some((v) => v.option2 === size && v.inventoryQuantity > 0);
    },
    [product.variants, opt1IsSize],
  );

  const handleQuickAdd = useCallback(
    (size: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isSizeInStock(size)) return;

      const variant = opt1IsSize
        ? product.variants.find((v) => v.option1 === size && v.inventoryQuantity > 0)
        : product.variants.find((v) => v.option2 === size && v.inventoryQuantity > 0);
      if (!variant) return;

      addToCart({
        variantId: variant.id,
        productHandle: product.handle || product.id,
        productTitle: product.title,
        variantTitle: opt1IsSize ? size : `${variant.option1 || ""} / ${size}`.trim(),
        color: opt1IsSize ? "" : (variant.option1 || ""),
        size,
        price: Number(variant.price),
        comparePrice: variant.compareAtPrice ? Number(variant.compareAtPrice) : undefined,
        // Use the resolver (mediaOverrides-first) so the cart thumbnail shows
        // the admin-curated image, not the raw featuredImageUrl that a Sapo
        // sync reverts to bizweb. Falls back to featuredImageUrl internally.
        imageUrl:
          resolveFeaturedImage((activeSibling ?? product) as any) ||
          product.featuredImageUrl ||
          undefined,
        quantity: 1,
        // Inventory cap captured at add time so the cart drawer's
        // +/− buttons + checkout quantity field can't exceed stock.
        maxQuantity: variant.inventoryQuantity,
      });

      setAddedSize(size);
      setTimeout(() => setAddedSize(null), 1500);
    },
    [product, addToCart, isSizeInStock],
  );

  // Build badge list (max 2)
  const badges: { text: string; className: string }[] = [];
  if (isPreOrder) {
    badges.push({
      text: locale === "en" ? "Pre-Order" : "\u0110\u1eb7t Tr\u01b0\u1edbc",
      className: "sf-badge-new", // reuse "new" style
    });
  }
  if (isSoldOut) {
    badges.push({
      text: locale === "en" ? "Sold Out" : "H\u1ebft H\u00e0ng",
      className: "sf-badge-selling-fast",
    });
  }
  if (badges.length < 2 && isNew && !isPreOrder) {
    badges.unshift({ text: locale === "en" ? "New" : "M\u1edbi", className: "sf-badge-new" });
  }
  if (badges.length < 2 && lowStock && !isNew && !isSoldOut && !isPreOrder) {
    badges.push({ text: t("sellingFast", locale), className: "sf-badge-selling-fast" });
  }
  if (badges.length < 2 && comparePrice && Math.round((1 - price / comparePrice) * 100) > 0) {
    badges.push({
      text: `${Math.round((1 - price / comparePrice) * 100)}% ${t("off", locale)}`,
      className: "sf-badge-sale",
    });
  }
  // Cap at 2
  while (badges.length > 2) badges.pop();

  // Sibling swatches (dedup by color)
  const siblingSwatches = useMemo(() => {
    if (!siblings || siblings.length <= 1) return [];
    const seen = new Set<string>();
    return siblings.filter((s) => {
      if (!s.color || seen.has(s.color)) return false;
      seen.add(s.color);
      return true;
    });
  }, [siblings]);

  const visibleSwatches = siblingSwatches.slice(0, MAX_SWATCHES);
  const extraSwatchCount = siblingSwatches.length - MAX_SWATCHES;

  return (
    <div
      className="sf-product-card group pb-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setCurrentImageIndex(0);
      }}
    >
      <Link to={`/products/${displayHandle}`} onClick={handleLinkClick}>
        <div className="sf-card-image">
          {imageList.length > 0 ? (
            <div
              ref={cardRef}
              className="relative w-full h-full overflow-hidden"
              // `touch-action: pan-y` — browser only does vertical pan
              // on this element by default. JS handles horizontal swipe
              // (the image carousel). Combined with the useEffect-attached
              // non-passive touchmove listener above (which preventDefaults
              // once horizontal drag is committed), this stops the vertical
              // jitter merchants reported when swiping across product
              // images on mobile.
              style={{ touchAction: imageList.length > 1 ? "pan-y" : undefined }}
              onTouchStart={imageList.length > 1 ? handleTouchStart : undefined}
              onTouchMove={imageList.length > 1 ? handleTouchMove : undefined}
              onTouchEnd={imageList.length > 1 ? handleTouchEnd : undefined}
            >
              {/* Sliding image strip */}
              <div
                className={`flex h-full ${isTransitioning ? "transition-transform duration-300 ease-out" : ""}`}
                style={{
                  width: `${imageList.length * 100}%`,
                  transform: `translateX(calc(-${currentImageIndex * (100 / imageList.length)}% + ${dragOffset}px))`,
                }}
              >
                {imageList.map((url, i) => (
                  <div key={i} className="relative h-full" style={{ width: `${100 / imageList.length}%` }}>
                    {/* T2.14: explicit width/height tells the browser the intrinsic
                        4:5 aspect ratio so it can reserve space before the image
                        loads. The wrapping `.sf-card-image` div already enforces
                        the aspect via CSS, but adding the HTML attributes also
                        eliminates the CLS that shows up in Lighthouse for
                        progressively-loaded images. */}
                    <img
                      src={url}
                      alt={i === 0 ? displayTitle : `${displayTitle} - view ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      width={400}
                      height={500}
                      className="absolute inset-0 w-full h-full object-cover"
                      // Backstop: a deleted/404 image hides itself so the card
                      // shows its gray placeholder, never a broken-image icon.
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Dot indicators */}
              {imageList.length > 1 && (
                <div className="sf-card-dots">
                  {imageList.map((_, i) => (
                    <button
                      key={i}
                      className={`sf-card-dot ${i === currentImageIndex ? "sf-card-dot-active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentImageIndex(i);
                      }}
                      aria-label={`View image ${i + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Desktop arrow buttons */}
              {imageList.length > 1 && (
                <>
                  {currentImageIndex > 0 && (
                    <button
                      className="sf-card-arrow sf-card-arrow-left"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => prev - 1);
                      }}
                      aria-label="Previous image"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  {currentImageIndex < imageList.length - 1 && (
                    <button
                      className="sf-card-arrow sf-card-arrow-right"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => prev + 1);
                      }}
                      aria-label="Next image"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="w-full h-full bg-[#f5f5f5]" />
          )}

          {/* Badges — top-left, transparent background */}
          {badges.length > 0 && (
            <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
              {badges.map((b, i) => (
                <span key={i} className="text-[11px] text-black/70 px-1 py-0.5">{b.text}</span>
              ))}
            </div>
          )}

        </div>
      </Link>

      {/* Quick View bar — sits BELOW the product image, never overlaps.
          Audit 2026-05-15: previously absolute-positioned at the bottom
          of the image with a translucent background (Audit 2026-05-11,
          Clarity finding). Merchant feedback that the overlay clipped
          model feet in product photography — moved out of the image
          container entirely so the button is now a flow-level sibling
          between the image and the product info. Still always-visible
          on mobile (touch-device shoppers); hover-reveal on desktop so
          the listing stays clean until the customer engages.

          Outside the wrapping Link, so no preventDefault/stopPropagation
          dance needed — the click can't bubble to a navigation anyway. */}
      {onQuickView && (
        <button
          type="button"
          className="mt-2 w-full text-center py-1.5 text-[11px] sm:text-[12px] uppercase tracking-[0.06em] text-gray-600 hover:text-black transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
          onClick={onQuickView}
        >
          {t("quickView", locale)}
        </button>
      )}

      {/* Product Info — Diolo rebrand 2026-06-10: single meta row (name
          left / price right), colour subtitle, dot swatches + count. */}
      <div className="mt-3 px-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <Link to={`/products/${displayHandle}`} className="flex-1 min-w-0">
            {/* h2 (not h3) keeps the listing hierarchy h1 → h2. */}
            <h2 className="text-[12px] font-medium text-black truncate">{displayBaseTitle}</h2>
          </Link>
          <span className="shrink-0 text-[12px] font-normal">
            {comparePrice && price < comparePrice ? (
              <>
                <span className="text-gray-400 line-through mr-1.5">{formatStorefrontPrice(comparePrice, currency)}</span>
                <span className="text-black">{formatStorefrontPrice(price, currency)}</span>
              </>
            ) : (
              <span className="text-black">{formatStorefrontPrice(price, currency)}</span>
            )}
          </span>
          {/* Bookmark/save — T2.17: guests get bounced to login with a
              redirect back to this listing. */}
          <button
            className={`flex-shrink-0 transition-colors ${isWishlisted ? "text-black" : "text-gray-300 hover:text-black"}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!toggleWishlist) {
                const returnTo = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
                if (typeof window !== "undefined") {
                  window.location.href = `/account/login?redirect=${encodeURIComponent(returnTo)}`;
                }
                return;
              }
              if (!isWishlisted) {
                setWishlistBounce(true);
                setTimeout(() => setWishlistBounce(false), 600);
              }
              toggleWishlist(product.id);
            }}
            aria-label={isWishlisted ? "Remove from saved" : "Save"}
          >
            <svg className="w-3.5 h-3.5" fill={isWishlisted ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
          </button>
        </div>
        {/* Colour subtitle */}
        {displayColourName && (
          <p className="mt-0.5 text-[12px] font-normal capitalize text-[#737373] truncate">{displayColourName}</p>
        )}

        {/* Sibling color swatches (interactive — Aritzia-sized).
            Includes the current product's own colour so the buyer can always
            see "I'm looking at the Pink one, here are the other colours". */}
        {siblingSwatches.length > 1 && (
          <div
            className="flex items-center gap-1 mt-1.5"
            // Reset hover preview when the cursor leaves the whole swatch
            // row — moving between swatches inside the row keeps the
            // preview smooth instead of flickering back to the base image.
            onMouseLeave={() => setHoveredSiblingId(null)}
          >
            {visibleSwatches.map((s) => {
              const isCurrent = !!(s as any).isCurrent;
              const isPreviewed = selectedSiblingId === s.id;
              const isSelected = isCurrent ? !selectedSiblingId : isPreviewed;
              return (
                <button
                  key={s.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isCurrent) {
                      // Clicking the current colour clears any sibling preview.
                      setSelectedSiblingId(null);
                    } else {
                      setSelectedSiblingId(selectedSiblingId === s.id ? null : s.id);
                    }
                  }}
                  // Aritzia-style hover preview: hovering a swatch surfaces
                  // that variant's gallery in the card without committing
                  // the click. Hovering the current swatch previews the
                  // base product (sentinel "__base__").
                  onMouseEnter={() => setHoveredSiblingId(isCurrent ? "__base__" : s.id)}
                  className={`w-2.5 h-2.5 rounded-full border transition-all ${
                    isSelected ? "border-black scale-125" : "border-black/10 hover:border-gray-500"
                  }`}
                  style={{ backgroundColor: getColorHex(s.color) }}
                  title={s.color!}
                  aria-label={s.color!}
                />
              );
            })}
            <span className="ml-1 text-[10px] text-[#737373]">
              {siblingSwatches.length} {locale === "vi" ? "Màu" : siblingSwatches.length === 1 ? "Colour" : "Colours"}
            </span>
            {extraSwatchCount > 0 && (
              <Link
                to={`/products/${product.handle || product.id}`}
                className="text-[10px] text-gray-500 hover:text-black"
              >
                +{extraSwatchCount}
              </Link>
            )}
          </div>
        )}

        {/* Fallback: original color swatches when no siblings */}
        {siblingSwatches.length <= 1 && colors.length > 1 && (
          <div className="flex items-center gap-1.5 mt-1.5">
            {colors.slice(0, 6).map((c) => (
              <div
                key={c}
                className="w-2.5 h-2.5 rounded-full border border-black/10"
                style={{ backgroundColor: getColorHex(c) }}
                title={c}
                role="img"
                aria-label={c}
              />
            ))}
            <span className="ml-1 text-[10px] text-[#737373]">
              {colors.length} {locale === "vi" ? "Màu" : "Colours"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
